<template>
  <Transition name="book-share-poster-fade">
    <div
      v-if="props.visible"
      class="book-share-poster-modal"
      role="dialog"
      aria-modal="true"
      aria-label="长按图片保存海报"
      @pointerdown.self.stop
      @click.self="emit('close')"
    >
      <div class="book-share-poster-content">
        <div class="book-share-poster-hint">长按图片保存海报</div>
        <div class="book-share-poster-frame" @click.stop @pointerdown.stop>
          <img
            v-if="posterUrl"
            class="book-share-poster-image"
            :src="posterUrl"
            :alt="`《${displayTitle}》分享海报`"
            draggable="false"
          />
          <div v-else class="book-share-poster-loading" role="status">
            {{ posterError ? '海报生成失败，请稍后重试' : '海报生成中…' }}
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import brandLogoUrl from '@/assets/icons/svg/logo.svg?url';

  const props = defineProps<{
    visible: boolean;
    title: string;
    coverUrl?: string;
    qrCodeUrl?: string;
  }>();

  const emit = defineEmits<{
    close: [];
    error: [message: string];
  }>();

  const posterUrl = ref('');
  const posterError = ref(false);
  const displayTitle = computed(() => props.title || '未命名绘本');

  const POSTER_WIDTH = 308;
  const POSTER_HEIGHT = 481;
  const SCALE = 2;
  const COVER_X = 34;
  const COVER_Y = 60;
  const COVER_WIDTH = 240;
  const COVER_HEIGHT = 262;
  const INFO_TOP = 330;
  let renderToken = 0;

  function buildRoundedRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function fillRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    fillStyle: string | CanvasGradient
  ) {
    ctx.save();
    buildRoundedRectPath(ctx, x, y, width, height, radius);
    ctx.fillStyle = fillStyle;
    ctx.fill();
    ctx.restore();
  }

  function drawImageCover(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    const imageRatio = image.naturalWidth / image.naturalHeight;
    const boxRatio = width / height;
    const sourceWidth = imageRatio > boxRatio ? image.naturalHeight * boxRatio : image.naturalWidth;
    const sourceHeight = imageRatio > boxRatio ? image.naturalHeight : image.naturalWidth / boxRatio;
    const sourceX = (image.naturalWidth - sourceWidth) / 2;
    const sourceY = (image.naturalHeight - sourceHeight) / 2;

    ctx.save();
    buildRoundedRectPath(ctx, x, y, width, height, radius);
    ctx.clip();
    ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
    ctx.restore();
  }

  function trimToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
    let result = text;
    while (result && ctx.measureText(`${result}…`).width > maxWidth) {
      result = result.slice(0, -1);
    }
    return `${result}…`;
  }

  function drawWrappedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    maxLines: number
  ) {
    const chars = [...text];
    let line = '';
    let lineCount = 0;

    for (let index = 0; index < chars.length; index += 1) {
      const next = line + chars[index];
      const isLast = index === chars.length - 1;

      if (ctx.measureText(next).width > maxWidth && line) {
        lineCount += 1;
        const remaining = chars.slice(index).join('');
        const shouldEllipsis = lineCount === maxLines;
        ctx.fillText(shouldEllipsis ? trimToWidth(ctx, line + remaining, maxWidth) : line, x, y);
        if (shouldEllipsis) return;
        line = chars[index];
        y += lineHeight;
        continue;
      }

      line = next;
      if (isLast) ctx.fillText(line, x, y);
    }
  }

  function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      if (!src.startsWith('data:') && !src.startsWith('blob:')) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('image load failed'));
      image.src = src;
    });
  }

  async function loadOptionalImage(src?: string) {
    if (!src) return null;
    try {
      return await loadImage(src);
    } catch {
      return null;
    }
  }

  function drawPosterBackground(ctx: CanvasRenderingContext2D) {
    ctx.clearRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT);

    ctx.shadowColor = 'rgba(31, 18, 74, 0.14)';
    ctx.shadowBlur = 14;
    ctx.shadowOffsetY = 8;

    const baseGradient = ctx.createLinearGradient(0, 0, POSTER_WIDTH, POSTER_HEIGHT);
    baseGradient.addColorStop(0, '#eedcff');
    baseGradient.addColorStop(0.42, '#f4e8ff');
    baseGradient.addColorStop(0.72, '#fff0fb');
    baseGradient.addColorStop(1, '#ffffff');
    fillRoundedRect(ctx, 0, 0, POSTER_WIDTH, POSTER_HEIGHT, 20, baseGradient);

    const rightGlow = ctx.createRadialGradient(POSTER_WIDTH, 210, 0, POSTER_WIDTH, 210, 260);
    rightGlow.addColorStop(0, 'rgba(255, 220, 240, 0.9)');
    rightGlow.addColorStop(0.56, 'rgba(255, 232, 248, 0.45)');
    rightGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
    fillRoundedRect(ctx, 0, 0, POSTER_WIDTH, POSTER_HEIGHT, 20, rightGlow);

    ctx.shadowColor = 'transparent';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.72)';
    for (let y = 16; y < 250; y += 20) {
      for (let x = 14; x < POSTER_WIDTH; x += 20) {
        ctx.beginPath();
        ctx.arc(x, y, 1.1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    fillRoundedRect(ctx, 0, INFO_TOP, POSTER_WIDTH, POSTER_HEIGHT - INFO_TOP, 20, '#ffffff');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, INFO_TOP, POSTER_WIDTH, 40);

    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(0, INFO_TOP, 12, 0, Math.PI * 2);
    ctx.arc(POSTER_WIDTH, INFO_TOP, 12, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function drawCoverPlaceholder(ctx: CanvasRenderingContext2D) {
    const placeholderGradient = ctx.createLinearGradient(
      COVER_X,
      COVER_Y,
      COVER_X + COVER_WIDTH,
      COVER_Y + COVER_HEIGHT
    );
    placeholderGradient.addColorStop(0, '#faf7ff');
    placeholderGradient.addColorStop(1, '#dfd2ff');
    fillRoundedRect(ctx, COVER_X, COVER_Y, COVER_WIDTH, COVER_HEIGHT, 6, placeholderGradient);

    ctx.fillStyle = '#8d74d7';
    ctx.font = '500 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('AI 绘本', POSTER_WIDTH / 2, 196);
  }

  function drawPosterText(ctx: CanvasRenderingContext2D) {
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#222222';
    ctx.font = '600 18px sans-serif';
    drawWrappedText(ctx, displayTitle.value, 34, 340, 238, 24, 2);

    ctx.strokeStyle = '#d9d2e7';
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(26, 370);
    ctx.lineTo(282, 370);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#333333';
    ctx.font = '400 15px sans-serif';
    ctx.fillText('扫码即可观看完整绘本', 30, 398);
    ctx.fillStyle = '#999999';
    ctx.font = '400 11px sans-serif';
    ctx.fillText('-新用户免费制作-', 30, 421);
  }

  function drawFooter(ctx: CanvasRenderingContext2D, logo: HTMLImageElement | null) {
    if (logo) {
      ctx.drawImage(logo, 38, 445, 92, 21);
    } else {
      ctx.fillStyle = '#111111';
      ctx.font = '600 13px sans-serif';
      ctx.fillText('绘本创作', 38, 449);
    }

    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#aaa4b5';
    ctx.font = '400 9px sans-serif';
    ctx.fillText('www.huiben.yunzhan365.com', 282, 458);
  }

  async function renderPoster() {
    const current = ++renderToken;
    posterUrl.value = '';
    posterError.value = false;

    if (!props.qrCodeUrl) return;

    try {
      await document.fonts?.ready;
      const [cover, qr, logo] = await Promise.all([
        loadOptionalImage(props.coverUrl),
        loadImage(props.qrCodeUrl),
        loadOptionalImage(brandLogoUrl)
      ]);
      if (current !== renderToken) return;

      const canvas = document.createElement('canvas');
      canvas.width = POSTER_WIDTH * SCALE;
      canvas.height = POSTER_HEIGHT * SCALE;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas context unavailable');

      ctx.scale(SCALE, SCALE);
      drawPosterBackground(ctx);

      if (cover) {
        drawImageCover(ctx, cover, COVER_X, COVER_Y, COVER_WIDTH, COVER_HEIGHT, 6);
      } else {
        drawCoverPlaceholder(ctx);
      }

      drawPosterText(ctx);
      fillRoundedRect(ctx, 238, 388, 54, 54, 2, '#ffffff');
      ctx.drawImage(qr, 240, 390, 50, 50);
      drawFooter(ctx, logo);

      posterUrl.value = canvas.toDataURL('image/png');
    } catch (error) {
      if (current !== renderToken) return;
      posterError.value = true;
      emit('error', error instanceof Error ? error.message : 'poster render failed');
    }
  }

  watch(
    () => [props.visible, props.title, props.coverUrl, props.qrCodeUrl] as const,
    ([visible]) => {
      if (!visible) {
        renderToken += 1;
        posterUrl.value = '';
        posterError.value = false;
        return;
      }

      void renderPoster();
    },
    { immediate: true }
  );
</script>

<style scoped>
  /*
   * 海报展示按「含 chrome 的可用视口」等比缩放：
   * - 宽高同时约束，避免横屏/矮视口只按宽度算高导致裁切；
   * - 用 % 相对 modal（含强制横屏 transform 后的 containing block），不用 vw/vh。
   */
  .book-share-poster-modal {
    --poster-pad-y: 24px;
    --poster-pad-x: 16px;
    --poster-hint-h: 34px;
    --poster-gap: 34px;
    position: fixed;
    inset: 0;
    z-index: 3200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--poster-pad-y) var(--poster-pad-x);
    background: rgba(0, 0, 0, 0.58);
    box-sizing: border-box;
  }

  .book-share-poster-content {
    width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--poster-gap);
    pointer-events: none;
    box-sizing: border-box;
  }

  .book-share-poster-hint {
    flex-shrink: 0;
    width: min(100%, 308px);
    min-height: var(--poster-hint-h);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    line-height: 20px;
    pointer-events: none;
  }

  /* 步骤1：可用高度 = 内容区高度 − 提示 − 间距；步骤2：宽度取宽/高约束的较小值 */
  .book-share-poster-frame {
    --poster-max-h: calc(100% - var(--poster-hint-h) - var(--poster-gap));
    flex: 0 1 auto;
    width: min(82.4%, 322px, calc(var(--poster-max-h) * 308 / 481));
    max-height: var(--poster-max-h);
    aspect-ratio: 308 / 481;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
  }

  .book-share-poster-image {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 20px;
    object-fit: contain;
    user-select: none;
    -webkit-touch-callout: default;
  }

  .book-share-poster-loading {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    font-size: 14px;
    line-height: 22px;
  }

  /* 遮罩淡入淡出 + 海报轻微上浮缩放，进退场更顺滑 */
  .book-share-poster-fade-enter-active,
  .book-share-poster-fade-leave-active {
    transition: opacity 280ms ease;
  }

  .book-share-poster-fade-enter-active .book-share-poster-content,
  .book-share-poster-fade-leave-active .book-share-poster-content {
    transition:
      transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 280ms ease;
  }

  .book-share-poster-fade-enter-from,
  .book-share-poster-fade-leave-to {
    opacity: 0;
  }

  .book-share-poster-fade-enter-from .book-share-poster-content,
  .book-share-poster-fade-leave-to .book-share-poster-content {
    opacity: 0;
    transform: translateY(16px) scale(0.94);
  }

  /* 矮视口（含手机横屏）：收紧边距与间距，把空间留给海报本体 */
  @media (max-height: 720px) {
    .book-share-poster-modal {
      --poster-pad-y: 12px;
      --poster-gap: 16px;
    }
  }

  @media (max-height: 420px) {
    .book-share-poster-modal {
      --poster-pad-y: 8px;
      --poster-gap: 10px;
      --poster-hint-h: 28px;
    }

    .book-share-poster-hint {
      font-size: 12px;
      line-height: 18px;
    }

    .book-share-poster-fade-enter-from .book-share-poster-content,
    .book-share-poster-fade-leave-to .book-share-poster-content {
      transform: translateY(10px) scale(0.96);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .book-share-poster-fade-enter-active,
    .book-share-poster-fade-leave-active,
    .book-share-poster-fade-enter-active .book-share-poster-content,
    .book-share-poster-fade-leave-active .book-share-poster-content {
      transition: none;
    }
  }
</style>
